import React, { useMemo } from 'react';
import { ColumnConfig, RichTableState, TableConfig } from './types';
import * as Filtering from '../../shared/Filtering';
import { getIntl } from '../../../utils/intl';
import { View } from 'react-native';
import Chip from '../Chip';
import Stack from '../Stack';
import CollapsableContent from './CollapsableContent';
import Text from '../Text';
import { TABLE_PADDING } from './utils';
import { PRScrollView } from '../../shared/Scroll';

const intl = getIntl('RichTableSearchFilters', {
    search: 'Search...',
    filters: 'Filters',
    filterBy: 'Filter By:',
});

export type SearchFiltersOwnProps = {
    data: any[];
    config: TableConfig<any>;
    state: RichTableState;
    onStateChange: (newState: RichTableState) => void;
};

export type SearchFiltersProps = SearchFiltersOwnProps;

const getDiscreetValuesForColumn = (columnConfig: ColumnConfig<any>, data: any[]): Set<string> => {
    const values = new Set<string>();
    if (!columnConfig.getDiscreetValue) return values;

    for (const dataElement of data) {
        const value = columnConfig.getDiscreetValue(dataElement);
        if (value !== null && value !== undefined && value !== '') {
            const asArray = Array.isArray(value) ? value : [value];
            for (const item of asArray) {
                values.add(item);
            }
        }
    }

    return values;
};

export const SEARCH_FILTER_HEIGHT = 44;

const SearchFilters: React.FC<SearchFiltersProps> = (props) => {
    const enableSearch = useMemo(() => {
        return props.config.columns.some((column) => column.getSearchValue);
    }, [props.config.columns]);
    const enableDiscreetFilters = useMemo(() => {
        for (const column of props.config.columns) {
            const values = getDiscreetValuesForColumn(column, props.data);

            if (values.size) {
                return true;
            }
        }
    }, [props.config.columns, props.data]);

    if (!enableSearch && !enableDiscreetFilters) {
        return null;
    }

    const isDiscreetFilterEmpty = () => {
        for (const key of Object.keys(props.state.columnFilters)) {
            if (props.state.columnFilters[key]?.selectedDiscreetValues?.length) {
                return false;
            }
        }

        return true;
    };

    const getDiscreetFilters = () => {
        const returnValue: React.ReactNode[] = [];
        for (const column of props.config.columns) {
            const values = getDiscreetValuesForColumn(column, props.data);
            if (values.size === 0) continue;

            returnValue.push(
                <Filtering.MultiSelectFilter
                    key={column.columnKey}
                    title={column.label}
                    options={Array.from(values).map((value) => ({
                        value,
                        label: value,
                    }))}
                    value={props.state.columnFilters[column.columnKey]?.selectedDiscreetValues || []}
                    onChange={(selectedValues) =>
                        props.onStateChange({
                            ...props.state,
                            columnFilters: {
                                ...props.state.columnFilters,
                                [column.columnKey]: {
                                    selectedDiscreetValues: selectedValues || [],
                                },
                            },
                        })
                    }
                />,
            );
        }

        return returnValue;
    };

    const getFilterChips = () => {
        const returnValue: React.ReactNode[] = [];
        for (const key of Object.keys(props.state.columnFilters)) {
            const column = props.config.columns.find((column) => column.columnKey === key);
            if (column && props.state.columnFilters[key]?.selectedDiscreetValues?.length) {
                for (const value of props.state.columnFilters[key]?.selectedDiscreetValues || []) {
                    const existingColumnFilterValue = props.state.columnFilters[key]?.selectedDiscreetValues;
                    const columnFilterValueAfterRemoval = existingColumnFilterValue?.filter(
                        (selectedValue) => selectedValue !== value,
                    );
                    returnValue.push(
                        <Chip
                            key={`${key}-${value}`}
                            title={value}
                            highlight
                            onDelete={() => {
                                const updatedState: RichTableState = {
                                    ...props.state,
                                    columnFilters: {
                                        ...props.state.columnFilters,
                                        [key]: {
                                            selectedDiscreetValues: columnFilterValueAfterRemoval,
                                        },
                                    },
                                };

                                props.onStateChange(updatedState);
                            }}
                        />,
                    );
                }
            }
        }

        return returnValue;
    };

    const filterChips = getFilterChips();

    return (
        <Stack vertical align="flex-start" style={{ paddingLeft: TABLE_PADDING, paddingRight: TABLE_PADDING }}>
            <View style={{ width: 300, height: SEARCH_FILTER_HEIGHT }}>
                <Filtering.Container
                    renderSearch={() => (
                        <Filtering.SearchBar
                            value={props.state.searchValue}
                            onChange={(searchText) =>
                                props.onStateChange({
                                    ...props.state,
                                    searchValue: searchText,
                                })
                            }
                            testID="rich-table-search"
                            placeholder={intl('search')}
                            isDisabled={!props.data.length || !enableSearch}
                        />
                    )}
                    renderFilterButton={() =>
                        !enableDiscreetFilters ? null : (
                            <Filtering.FilterButton
                                filterPromptTitle={intl('filters')}
                                onResetFilters={() =>
                                    props.onStateChange({ ...props.state, searchValue: '', columnFilters: {} })
                                }
                                renderFilterContents={() => (
                                    <Filtering.FilterPopoverContent resetDisabled={isDiscreetFilterEmpty()}>
                                        {getDiscreetFilters()}
                                    </Filtering.FilterPopoverContent>
                                )}
                            />
                        )
                    }
                />
            </View>
            <CollapsableContent isCollapsed={!filterChips.length} expandedHeight={50}>
                <PRScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <Stack horizontal space={2.5} align="center" justify="flex-start">
                        <Text variant="body-1" color="Gray-Text">
                            {intl('filterBy')}
                        </Text>
                        {filterChips}
                    </Stack>
                </PRScrollView>
            </CollapsableContent>
        </Stack>
    );
};

export default React.memo(SearchFilters);
